import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import DvfOverlay from './DvfOverlay';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    deformationArrowSpacingMmState,
    deformationArrowsEnabledState,
    deformationDisplayRangeMmState,
    deformationGridEnabledState,
    deformationGridSpacingMmState,
} from '~/state/atoms/deformationDisplayAtoms';
import { useBodyOutline } from '~/hooks/viewer/useBodyOutline';
import { useSelectedDeformationField } from '~/hooks/viewer/useSelectedDeformationField';

interface DeformationFieldOverlayProps {
    view: PlaneView | null;
}

export default function DeformationFieldOverlay({
    view,
}: DeformationFieldOverlayProps) {
    const field = useSelectedDeformationField();
    const arrows = useRecoilValue(deformationArrowsEnabledState);
    const grid = useRecoilValue(deformationGridEnabledState);
    const arrowSpacingMm = useRecoilValue(deformationArrowSpacingMmState);
    const gridSpacingMm = useRecoilValue(deformationGridSpacingMmState);
    const displayRange = useRecoilValue(deformationDisplayRangeMmState);
    const image = view?.environment.image?.parent;
    // Segmented only when something would be drawn over it.
    const { insideBody, basis } = useBodyOutline(
        image,
        !!field && (arrows || grid)
    );

    // The image's acquisition field of view, not the deformation's cropped box.
    const bounds = useMemo(() => {
        const box = image?.grid.toAxisAlignedBoundingBox();
        return box?.min && box.max ? { min: box.min, max: box.max } : undefined;
    }, [image]);

    return (
        <DvfOverlay
            view={view}
            field={field}
            arrows={arrows}
            grid={grid}
            arrowSpacingMm={arrowSpacingMm}
            gridSpacingMm={gridSpacingMm}
            rangeLowMm={displayRange?.lowMm}
            rangeHighMm={displayRange?.highMm}
            bounds={bounds}
            insideBody={insideBody}
            basis={basis}
        />
    );
}
